import {
  closeCurrentDialog,
  Form,
  FormControl,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Text, VStack} from 'platform/foundation';

import {useWatch} from 'react-hook-form';

import {isEmpty, isNotEmpty, uniq} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetMakeModelWithMakeQuery, useRemapCustomModelFamilyMutation} from '@dms/api/sales';
import {getApiErrorMessages} from '@dms/api/utils';
import i18n, {FALLBACK_LANGUAGE} from '@dms/i18n';
import {getVehicleLabels} from '@dms/shared';

import {ApiError, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  CustomCatalogueMakeGroup,
  CustomCatalogueModel,
  getCustomModels,
  getMakeGroupKey,
} from '../utils/groupUsageByMake';
import {
  KEEP_ORIGINAL_MODEL_FAMILY,
  remapModelsFormSchema,
  RemapModelsFormValues,
} from '../utils/remapMakeFormSchema';
import {ModelFamilyMappingFields} from './ModelFamilyMappingFields';
import {RemapFormFooter} from './RemapFormFooter';

interface RemapModelsDialogProps extends RequiredTestIdProps {
  /** The group's make is always an official catalogue value - a custom make is remapped as a whole. */
  group: CustomCatalogueMakeGroup;
  onSubmitted: (key: string) => void;
}

export function RemapModelsDialog(props: RemapModelsDialogProps) {
  const [remapCustomModelFamily] = useRemapCustomModelFamilyMutation();

  const models = getCustomModels(props.group);

  const defaultValues: RemapModelsFormValues = {
    modelFamilyMapping: models.map((model) => ({
      modelFamily: model.modelFamily,
      targetModelFamily: '',
    })),
  };

  // One request per changed model, a kept one is not sent. Settled, not all: a 202 is already
  // running in the worker, so another request failing must not hide it or leave the row unlocked.
  const handleSubmit: FormSubmitHandler<RemapModelsFormValues> = async (
    values,
    _setErrors,
    reset
  ) => {
    const changed = values.modelFamilyMapping.filter(
      (item) => item.targetModelFamily !== KEEP_ORIGINAL_MODEL_FAMILY
    );

    const results = await Promise.allSettled(
      changed.map((item) =>
        remapCustomModelFamily({
          vehicleType: props.group.vehicleType,
          make: props.group.make,
          modelFamily: item.modelFamily,
          targetModelFamily: item.targetModelFamily,
        }).unwrap()
      )
    );

    const acceptedModelFamilies = changed
      .filter((_, index) => results[index].status === 'fulfilled')
      .map((item) => item.modelFamily);

    if (isNotEmpty(acceptedModelFamilies)) {
      // 202 Accepted: nothing is done yet and no count is known - the toast reports both.
      props.onSubmitted(getMakeGroupKey(props.group));
      showNotification.info(i18n.t('entity.customCatalogue.notifications.replacementInProgress'));
    }

    const rejected = results.filter(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    );

    if (isEmpty(rejected)) {
      closeCurrentDialog();
      return;
    }

    // One toast for all of them - a toast per failed model would stack up.
    showNotification.error(
      uniq(rejected.flatMap((result) => getApiErrorMessages(result.reason as ApiError)))
    );

    // Accepted models move to keep-original, so resubmitting the failed ones cannot resend them.
    reset({
      modelFamilyMapping: values.modelFamilyMapping.map((item) =>
        acceptedModelFamilies.includes(item.modelFamily)
          ? {...item, targetModelFamily: KEEP_ORIGINAL_MODEL_FAMILY}
          : item
      ),
    });
  };

  return (
    <Form<RemapModelsFormValues>
      experimentalZodSchema={remapModelsFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
    >
      {(control) => (
        <RemapModelsFormContent
          control={control}
          group={props.group}
          models={models}
          data-testid={suffixTestId('form', props)}
        />
      )}
    </Form>
  );
}

interface RemapModelsFormContentProps extends RequiredTestIdProps {
  control: FormControl<RemapModelsFormValues>;
  group: CustomCatalogueMakeGroup;
  models: CustomCatalogueModel[];
}

function RemapModelsFormContent(props: RemapModelsFormContentProps) {
  const modelFamilyMapping = useWatch({control: props.control, name: 'modelFamilyMapping'});

  // Same lang argument as the vehicle form hooks, so the cached entry is shared with them.
  const {data: modelCatalogue, isFetching: isModelCatalogueFetching} = useGetMakeModelWithMakeQuery(
    {
      vehicleType: props.group.vehicleType,
      make: props.group.make,
      lang: [i18n?.resolvedLanguage ?? FALLBACK_LANGUAGE],
    }
  );

  // Every model needs an explicit choice, and keeping all of them would make the submit a no-op.
  const isSubmitDisabled =
    modelFamilyMapping.some((item) => isNilOrEmpty(item.targetModelFamily)) ||
    modelFamilyMapping.every((item) => item.targetModelFamily === KEEP_ORIGINAL_MODEL_FAMILY);

  return (
    <VStack spacing={4}>
      <Text size="small">
        {i18n.t('entity.customCatalogue.replaceModelsDialog.description', {
          make: getVehicleLabels({make: props.group.make}, modelCatalogue).make ?? props.group.make,
        })}
      </Text>

      <ModelFamilyMappingFields
        control={props.control}
        models={props.models}
        modelCatalogue={modelCatalogue}
        isLoading={isModelCatalogueFetching}
        data-testid={props['data-testid']}
      />

      <RemapFormFooter
        control={props.control}
        isSubmitDisabled={isSubmitDisabled}
        data-testid={props['data-testid']}
      />
    </VStack>
  );
}
